import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Download,
  Calendar,
  Database,
  Activity,
  Pill,
  Heart,
  FileCode,
  Package,
  ChevronDown,
  Code2,
} from 'lucide-react';
import { ChargemasterFileViewer } from '@/components/ChargemasterFileViewer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

interface ChargemasterFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size_bytes: number;
  year: number;
  gcs_bucket_name?: string;
  gcs_path?: string;
  gcs_public_url: string;
  gcs_signed_url?: string;
  status: string;
  row_count?: number;
  header_row_number?: number;
  uploaded_at: string;
  import_script_name?: string;
  import_script_gcs_path?: string;
  import_script_gcs_url?: string;
}

interface CodeStats {
  cpt_count: number;
  drg_count: number;
  revenue_count: number;
  ndc_count: number;
  supply_count: number;
  hcpcs_count: number;
  total_count: number;
}

interface HospitalChargemastersTabProps {
  hospitalId: string;
  hospitalName: string;
}

export function HospitalChargemastersTab({ hospitalId, hospitalName }: HospitalChargemastersTabProps) {
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [importScriptCode, setImportScriptCode] = useState<string>('');
  const [loadingScript, setLoadingScript] = useState(false);

  // Fetch chargemaster files for this hospital
  const { data: files, isLoading: filesLoading } = useQuery({
    queryKey: ['chargemaster-files', hospitalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chargemaster_files')
        .select('id, file_name, file_type, file_size_bytes, year, gcs_bucket_name, gcs_path, gcs_public_url, gcs_signed_url, status, row_count, header_row_number, uploaded_at, import_script_name, import_script_gcs_path, import_script_gcs_url')
        .eq('hospital_id', hospitalId)
        .in('status', ['uploaded', 'processed', 'failed'])
        .order('year', { ascending: false })
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      return data as ChargemasterFile[];
    },
  });

  // Auto-select first file when loaded
  React.useEffect(() => {
    if (files && files.length > 0 && !selectedFileId) {
      setSelectedFileId(files[0].id);
    }
  }, [files, selectedFileId]);

  // Fetch code statistics for this hospital from hospital_ranking_summary (most accurate)
  const { data: codeStats, isLoading: statsLoading } = useQuery({
    queryKey: ['hospital-code-stats', hospitalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_ranking_summary')
        .select('cpt_procedure_count, drg_procedure_count, rev_procedure_count, ndc_procedure_count, supply_procedure_count, hcpcs_procedure_count, total_procedures')
        .eq('hospital_id', hospitalId)
        .maybeSingle();

      if (error) throw error;

      // Return pre-aggregated counts from hospital_ranking_summary
      const stats: CodeStats = {
        cpt_count: data?.cpt_procedure_count || 0,
        drg_count: data?.drg_procedure_count || 0,
        revenue_count: data?.rev_procedure_count || 0,
        ndc_count: data?.ndc_procedure_count || 0,
        supply_count: data?.supply_procedure_count || 0,
        hcpcs_count: data?.hcpcs_procedure_count || 0,
        total_count: data?.total_procedures || 0,
      };

      return stats;
    },
  });

  const formatBytes = (bytes: number | null | undefined): string => {
    // file_size_bytes is NULL for failed/never-fully-uploaded imports;
    // showing "NaN B" looks like a code bug. Render an em-dash instead.
    if (bytes == null || !Number.isFinite(bytes)) return '—';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const selectedFile = files?.find(f => f.id === selectedFileId);

  const codeTypeStats = [
    {
      label: 'CPT',
      count: codeStats?.cpt_count || 0,
      icon: Activity,
      color: '#60a5fa',
    },
    {
      label: 'DRG',
      count: codeStats?.drg_count || 0,
      icon: Heart,
      color: '#c084fc',
    },
    {
      label: 'REV',
      count: codeStats?.revenue_count || 0,
      icon: FileCode,
      color: '#facc15',
    },
    {
      label: 'NDC',
      count: codeStats?.ndc_count || 0,
      icon: Pill,
      color: '#fb7185',
    },
    {
      label: 'Supply',
      count: codeStats?.supply_count || 0,
      icon: Package,
      color: '#4ade80',
    },
    {
      label: 'HCPCS',
      count: codeStats?.hcpcs_count || 0,
      icon: FileText,
      color: '#14b8a6',
    },
  ];

  if (filesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading chargemaster files...</div>
      </div>
    );
  }

  // Check if this is a known non-compliant hospital
  const isNonCompliantHospital = hospitalId === '529b1550-b0bf-43fe-a4f6-471205b079f5'; // Essentia Health St. Mary's Medical Center

  if (!files || files.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
          <FileText className="w-12 h-12 text-muted-foreground" />
          <div className="text-center space-y-4">
            <h3 className="text-lg font-semibold mb-2">No Chargemaster Files</h3>
            {isNonCompliantHospital ? (
              <div className="max-w-2xl mx-auto space-y-3">
                <Badge variant="destructive" className="text-sm px-3 py-1">
                  Non-Compliant with CMS Price Transparency Rules
                </Badge>
                <p className="text-muted-foreground">
                  This hospital's chargemaster file does not comply with 45 CFR 180.50 requirements.
                </p>
                <div className="text-left bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-semibold text-destructive">Compliance Issues:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>DRG codes only contain insurance negotiated rates (no gross charges)</li>
                    <li>Missing uninsured/self-pay rates (standard_charge|discounted_cash field empty)</li>
                    <li>Only provides payer-specific pricing without standard charges</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-3">
                    <strong>File Reviewed:</strong> 410695604_Essentia-St-Marys-Medical-Center_standardcharges.csv
                  </p>
                  <p className="text-sm text-muted-foreground">
                    All pricing records have been excluded from our database as they do not represent actual hospital charges required for price comparison.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">
                No chargemaster files have been uploaded for this hospital yet.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* File Selector Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <FileText className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <Select value={selectedFileId || ''} onValueChange={setSelectedFileId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a chargemaster file" />
                  </SelectTrigger>
                  <SelectContent>
                    {files.map((file) => (
                      <SelectItem key={file.id} value={file.id}>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-xs">
                            {file.year}
                          </Badge>
                          <span>{file.file_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatBytes(file.file_size_bytes)})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedFile && (
              <div className="flex gap-2">
                {selectedFile.import_script_gcs_url && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          setLoadingScript(true);
                          try {
                            const response = await fetch(selectedFile.import_script_gcs_url!);
                            const code = await response.text();
                            setImportScriptCode(code);
                          } catch (error) {
                            console.error('Error loading script:', error);
                            toast.error('Failed to load import script');
                          } finally {
                            setLoadingScript(false);
                          }
                        }}
                      >
                        <Code2 className="w-4 h-4 mr-2" />
                        Import Script
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-5xl max-h-[90vh]">
                      <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                          <span>Import Script: {selectedFile.import_script_name}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(selectedFile.import_script_gcs_url, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </DialogTitle>
                      </DialogHeader>
                      <div className="overflow-auto max-h-[70vh]">
                        {loadingScript ? (
                          <div className="flex items-center justify-center p-12">
                            <div className="text-gray-500">Loading script...</div>
                          </div>
                        ) : (
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                            <code className="font-mono">{importScriptCode}</code>
                          </pre>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(selectedFile.gcs_public_url, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            )}
          </div>
          {selectedFile && (
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Uploaded {formatDate(selectedFile.uploaded_at)}
              </span>
              {selectedFile.row_count && (
                <span>{selectedFile.row_count.toLocaleString()} rows</span>
              )}
              <Badge variant="secondary" className="text-xs">
                {selectedFile.file_type.toUpperCase()}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content: File Preview (70%) + Statistics (30%) */}
      <div className="grid grid-cols-1 xl:grid-cols-10 gap-6">
        {/* File Preview - Takes up 70% on large screens */}
        <div className="xl:col-span-7">
          {selectedFile ? (
            <div className="h-[800px]">
              <ChargemasterFileViewer
                fileUrl={
                  selectedFile.gcs_public_url ||
                  selectedFile.gcs_signed_url ||
                  // Fallback: construct URL from gcs_path if both URLs are missing
                  (selectedFile.gcs_path
                    ? `https://storage.googleapis.com/${selectedFile.gcs_bucket_name || 'hall-of-shame'}/${selectedFile.gcs_path}`
                    : '')
                }
                fileName={selectedFile.file_name}
                fileType={selectedFile.file_type}
                fileId={selectedFile.id}
                headerRowNumber={selectedFile.header_row_number || 1}
                onHeaderRowChange={async (newRow) => {
                  // Update database
                  const { error } = await supabase
                    .from('chargemaster_files')
                    .update({ header_row_number: newRow })
                    .eq('id', selectedFile.id);

                  if (error) {
                    console.error('Error updating header row:', error);
                  }
                }}
              />
            </div>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center h-[800px]">
                <div className="text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select a file to preview</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Statistics Sidebar - Takes up 30% on large screens */}
        <div className="xl:col-span-3 space-y-4">
          {/* "Failed import" notice — soft framing, NOT a defamation-tier
              compliance accusation. Previously this card asserted the
              hospital was Non-Compliant with 45 CFR 180.50 whenever ANY
              file row had status='failed', which over-triggered: a failed
              import is most often a parsing/network issue on our side, not
              the hospital actually being out of compliance. The earlier
              isNonCompliantHospital code path above handles verified
              non-compliance cases via an explicit allowlist. */}
          {selectedFile?.status === 'failed' && (
            <Card className="border-amber-300/50 bg-amber-50/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-amber-700">
                  Import did not complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  This file was uploaded but our pipeline couldn't extract pricing data from it. Common causes
                  include encoding issues, unrecognized column layouts, or the file being a placeholder /
                  redirect page rather than the actual chargemaster. We retain the row so we can re-attempt
                  ingestion later.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Total Procedures */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4 text-primary" />
                Code Mapping Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-primary mb-1">
                  {codeStats?.total_count.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground">Total Procedures</p>
              </div>

              {/* Code Type Breakdown */}
              <div className="space-y-2">
                {codeTypeStats.map((stat) => {
                  const Icon = stat.icon;
                  const percentage = codeStats?.total_count
                    ? ((stat.count / codeStats.total_count) * 100).toFixed(1)
                    : '0.0';

                  return (
                    <div
                      key={stat.label}
                      className="flex items-center justify-between p-2 rounded border border-border hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: stat.color }} />
                        <span className="text-sm font-medium">{stat.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold" style={{ color: stat.color }}>
                          {stat.count.toLocaleString()}
                        </span>
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={{
                            borderColor: stat.color,
                            color: stat.color,
                          }}
                        >
                          {percentage}%
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* File Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">File Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Hospital</div>
                <div className="text-sm font-medium">{hospitalName}</div>
              </div>
              {selectedFile && (
                <>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">File Name</div>
                    <div className="text-sm font-medium break-all">{selectedFile.file_name}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Year</div>
                      <Badge variant="secondary">{selectedFile.year}</Badge>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Size</div>
                      <div className="text-sm font-medium">{formatBytes(selectedFile.file_size_bytes)}</div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Note */}
          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Note:</strong> These statistics show unique medical codes imported from chargemaster files. Multiple procedures may use the same code with different prices or settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}